<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :moduleEnabler="moduleEnabler"
    :inputs="inputs"
    :is-fetched="isFetched"
    :has-buttons="false"
  >
    <template #grid_data_source.input="data">
      <v-col cols="3">
        <data-input v-bind="data" v-model="grid_data_source" />
      </v-col>
    </template>
    <template #district_data_source.input="data">
      <v-col cols="3">
        <data-input v-bind="data" v-model="district_data_source" @change="name_attribute = undefined" />
      </v-col>
    </template>
    <template #name_attribute.input="data">
      <v-col cols="3">
        <data-input v-bind="data" v-model="name_attribute" />
      </v-col>
    </template>
    <template #saveButton.input>
      <v-col cols="auto">
        <base-button
          style="padding-right: 24px; padding-left: 24px"
          translation-path="button.save"
          @click="saveDatasourceSettings"
          :loading="isSaving"
        ></base-button>
      </v-col>
    </template>
    <template #greyTabs.input>
      <grey-tabs v-model="tab" :tabs="tabItems" cols="auto" has-border>
        <v-tab-item v-for="item in tabItems" :key="item.name">
          <component :is="item.component" :type="item.type"></component>
        </v-tab-item>
      </grey-tabs>
    </template>
  </admin-wrapper>
</template>

<script>
import { get, call } from 'vuex-pathify';

import GreyTabs from '@/components/GreyTabs';
import AdminWrapper from '@/components/AdminWrapper';
import ZdmReportsTab from '@/components/ZdmReportsTab';
import ZdmReportsTabCustom from '@/components/ZDMReportsTabCustom';
import DataInput from '@/components/DataInput';

export default {
  name: 'ZDMReports',
  components: {
    GreyTabs,
    DataInput,
    AdminWrapper,
    ZdmReportsTab,
    ZdmReportsTabCustom,
  },
  data: () => ({
    isFetched: false,
    isSaving: false,
    tab: 0,
    grid_data_source: undefined,
    district_data_source: undefined,
    name_attribute: undefined,
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules.ZDM_REPORTS.verbose_name'),
    modulesMapping: get('admin/modulesMapping'),
    dataSources: get('layers/metadata'),
    moduleEnabler() {
      return {
        name: 'zdm_reports_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    isReportGenerating() {
      return false;
    },
    tabItems() {
      return [
        {
          name: this.$i18n.t('admin.monthlyReports'),
          dataSource: 'investments_investment',
          disabled: false,
          type: 'monthly',
          component: 'ZdmReportsTab',
        },
        {
          name: this.$i18n.t('admin.customReports.tabTitle'),
          disabled: false,
          type: 'custom',
          component: 'ZdmReportsTabCustom',
        },
      ];
    },
    dataSourcesArray() {
      return this.$_objectToArray(this.dataSources);
    },
    polygonDataSources() {
      return this.dataSourcesArray.filter(ds => ds.geometry_type === 'polygon' || ds.geometry_type === 'multipolygon');
    },
    selectedDatasource() {
      return this.dataSources[this.district_data_source];
    },
    attributesSchema() {
      return this.selectedDatasource?.attributes_schema;
    },
    sortedSelectedDatasourceAttributes() {
      if (!this.attributesSchema) {
        return [];
      }
      const geom_name = this.attributesSchema.geometry_name;
      return this.attributesSchema.attributes.filter(v => v.verbose_name !== geom_name);
    },
    nameAttributeItems() {
      return this.sortedSelectedDatasourceAttributes.map(attribute => ({
        text: attribute.verbose_name,
        value: attribute.name,
      }));
    },
    dataSourcesItems() {
      return this.dataSourcesArray.map(ds => ({
        text: ds.verbose_name,
        value: ds.name,
      }));
    },
    inputs() {
      return [
        {
          name: 'grid_data_source',
          translationPath: 'admin.gridDataSource',
          subtitle: 'admin.gridDataSourceSubtitle',
          align: 'start',
          dataType: {
            name: 'select',
          },
          items: this.dataSourcesItems,
        },
        {
          name: 'district_data_source',
          translationPath: 'admin.districtsDataSource',
          subtitle: 'admin.districtsDataSourceSubtitle',
          align: 'start',
          dataType: {
            name: 'select',
          },
          items: this.dataSourcesItems,
        },
        {
          name: 'name_attribute',
          translationPath: 'admin.nameAttributeName',
          subtitle: 'admin.nameAttributeNameSubtitle',
          align: 'start',
          dataType: {
            name: 'select',
          },
          items: this.nameAttributeItems,
        },
        {
          name: 'saveButton',
          hasSuffix: false,
          isCustom: true,
          isNamedSlot: false,
        },
        {
          name: 'greyTabs',
          hasSuffix: false,
          isCustom: true,
          isNamedSlot: false,
        },
      ];
    },
  },
  methods: {
    getDataSources: call('layers/getLayersMetadata'),
    getDatasourceSettings: call('datasourceSettings/getDatasourceSettings'),
    editDatasourceSettings: call('datasourceSettings/editDatasourceSettings'),
    async saveDatasourceSettings() {
      this.isSaving = true;
      try {
        if (this.district_data_source !== undefined) {
          await this.editDatasourceSettings({
            name: 'zdm_reports_district',
            settings: {
              datasource: this.district_data_source,
              settings: {
                district_name: this.name_attribute,
              },
            },
          });
        }
        if (this.grid_data_source !== undefined) {
          await this.editDatasourceSettings({
            name: 'zdm_reports_grid',
            settings: {
              datasource: this.grid_data_source,
            },
          });
        }

        this.isSaving = false;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.setZdmReportData'),
        });
      } catch (e) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.setEmailSettingsFailed'),
        });
        this.isSaving = false;
      }
    },
    async init() {
      try {
        this.isFetched = false;
        await this.getDataSources({ with_attributes_schema: true });
        const r = await this.getDatasourceSettings('zdm_reports_district');
        this.district_data_source = r.datasource;
        this.name_attribute = r.settings.district_name;
        const { datasource } = await this.getDatasourceSettings('zdm_reports_grid');
        this.grid_data_source = datasource;
        this.isFetched = true;
      } catch (error) {
        this.isFetched = true;
      }
    },
  },
  created() {
    this.init();
  },
};
</script>

<style scoped lang="scss"></style>
